'use strict';

// Includes
const scrollElementBelowHeader = require('../utils/fixedHeaderScrollOffset').scrollElementBelowHeader;

/**
 * True when at least one product row has quantity greater than zero.
 * @param {jQuery} $productsContainer - Products container element.
 * @returns {boolean} Whether any quantity is positive.
 */
function hasAnyPositiveQuantity($productsContainer) {
    return $productsContainer.find('.js-withdrawal-quantity').toArray().some(function (el) {
        return Number($(el).val() || 0) > 0;
    });
}

/**
 * Scrolls an alert into view after layout (smooth).
 * @param {jQuery} $el - Alert element.
 * @returns {void}
 */
function scrollToWithdrawalMessage($el) {
    if (!$el || !$el.length) return;

    scrollElementBelowHeader($el[0]);
}

/**
 * Extracts selected products payload from rendered rows.
 * @param {jQuery} $productsContainer - Products container element.
 * @returns {Array} Selected product payload.
 */
function extractProducts($productsContainer) {
    const selected = [];

    $productsContainer.find('.js-withdrawal-product').each(function () {
        const $row = $(this);
        const quantity = Number($row.find('.js-withdrawal-quantity').val() || 0);

        if (quantity <= 0) return;

        const reason = $row.find('.js-withdrawal-reason:checked').val() || '';

        selected.push({
            productID: $row.data('product-id'),
            quantity: quantity,
            reason: reason
        });
    });

    return selected;
}

/**
 * Applies complete/partial behavior to quantity selectors.
 * @param {string} type - Selected withdrawal type.
 * @param {jQuery} $productsContainer - Products container element.
 * @returns {void}
 */
function setWithdrawalTypeMode(type, $productsContainer) {
    const $rows = $productsContainer.find('.js-withdrawal-product');

    $rows.each(function () {
        const $row = $(this);
        const max = Number($row.data('max-quantity')) || 0;
        const $quantity = $row.find('.js-withdrawal-quantity');

        if (type === 'complete') {
            $quantity.val(max).prop('readonly', true);
        } else {
            $quantity.val(0).prop('readonly', false);
        }
    });
}

/**
 * Hides the error alert.
 * @param {jQuery} $form - Withdrawal form element.
 * @returns {void}
 */
function hideError($form) {
    $form.find('.js-withdrawal-error').addClass('d-none').empty();
}

/**
 * Hides the success alert.
 * @param {jQuery} $form - Withdrawal form element.
 * @returns {void}
 */
function hideSuccess($form) {
    $form.find('.js-withdrawal-success').addClass('d-none').text('');
}

/**
 * Hides both alerts.
 * @param {jQuery} $form - Withdrawal form element.
 * @returns {void}
 */
function hideMessages($form) {
    hideError($form);
    hideSuccess($form);
}

/**
 * Shows error message and scrolls to it.
 * @param {jQuery} $form - Withdrawal form element.
 * @param {string} message - Error message.
 * @returns {void}
 */
function showError($form, message) {
    const $alert = $form.find('.js-withdrawal-error');
    const isSameMessageVisible = !$alert.hasClass('d-none') && $alert.html() === message;

    if (!isSameMessageVisible) {
        hideSuccess($form);
        $alert.removeClass('d-none').html(message);
    }

    scrollToWithdrawalMessage($alert);
}

/**
 * Shows success message and scrolls to it.
 * @param {jQuery} $form - Withdrawal form element.
 * @param {string} message - Success message.
 * @returns {void}
 */
function showSuccess($form, message) {
    const $alert = $form.find('.js-withdrawal-success');
    const isSameMessageVisible = !$alert.hasClass('d-none') && $alert.text() === message;

    if (!isSameMessageVisible) {
        hideError($form);
        $alert.removeClass('d-none').text(message);
    }

    scrollToWithdrawalMessage($alert);
}

module.exports = {
    /**
     * Initializes withdrawal form behaviors and AJAX flow.
     * @returns {void}
     */
    init: function () {
        const $form = $('.js-withdrawal-form');

        if (!$form.length) return;

        const $requiredFields = $form.find('.js-withdrawal-required');
        const $validateButton = $form.find('.js-withdrawal-validate');
        const $validateStep = $form.find('.js-withdrawal-validate-step');
        const $afterValidate = $form.find('.js-withdrawal-after-validate');
        const $typeGroup = $form.find('.js-withdrawal-type-group');
        const $productsContainer = $form.find('.js-withdrawal-products');
        const $policyCheckbox = $form.find('.js-withdrawal-policy');
        const $submitButton = $form.find('.js-withdrawal-submit');
        const $productsJson = $form.find('.js-products-json');

        const validateUrl = $validateButton.data('validate-url');
        const submitUrl = $submitButton.data('submit-url');

        let isValidated = false;

        /**
         * True when all order-validation fields are non-empty.
         * @returns {boolean} Whether validate can run.
         */
        function canValidateOrder() {
            return !$requiredFields.filter(function () {
                return !$(this).val();
            }).length;
        }

        /**
         * Enables/disables the explicit validate button.
         * @returns {void}
         */
        function updateValidateButtonState() {
            $validateButton.prop('disabled', !canValidateOrder());
        }

        /**
         * Builds AJAX payload for order validation request.
         * @returns {Object} Validation payload.
         */
        function buildValidationData() {
            return {
                csrf_token: $form.find('input[name="csrf_token"]').val(),
                firstName: $form.find('[name="firstName"]').val(),
                lastName: $form.find('[name="lastName"]').val(),
                email: $form.find('[name="email"]').val(),
                orderNo: $form.find('[name="orderNo"]').val()
            };
        }

        /**
         * Updates final submit button state.
         * @returns {void}
         */
        function updateSubmitState() {
            const hasType = !!$form.find('.js-withdrawal-type:checked').val();
            const isPolicyAccepted = $policyCheckbox.prop('checked');
            const hasProductRows = $productsContainer.find('.js-withdrawal-product').length > 0;
            const hasAnyQuantity = !hasProductRows || hasAnyPositiveQuantity($productsContainer);

            $submitButton.prop('disabled', !(isValidated && hasType && isPolicyAccepted && hasAnyQuantity));
        }

        /**
         * Hides step 2 and clears validation-dependent UI.
         * @returns {void}
         */
        function resetValidationState() {
            isValidated = false;

            $validateStep.removeClass('d-none');
            $afterValidate.addClass('d-none');
            $typeGroup.prop('disabled', true);
            $form.find('#withdrawal-type-complete').prop('checked', false);
            $form.find('#withdrawal-type-partial').prop('checked', true);
            $policyCheckbox.prop('checked', false);
            $productsContainer.empty().addClass('d-none');

            updateSubmitState();
            updateValidateButtonState();
        }

        updateValidateButtonState();

        $requiredFields.on('input change', function () {
            hideMessages($form);
            resetValidationState();
        });

        /**
         * Runs order validation AJAX and reveals step 2 on success.
         * @returns {void}
         */
        function runOrderValidation() {
            if (!canValidateOrder()) return;

            $validateButton.prop('disabled', true);

            $.ajax({
                url: validateUrl,
                method: 'POST',
                dataType: 'json',
                data: buildValidationData()
            }).done(function (response) {
                isValidated = true;
                hideError($form);

                $validateStep.addClass('d-none');
                $afterValidate.removeClass('d-none');
                $typeGroup.prop('disabled', false);
                $productsContainer.html(response.productsHtml || '').removeClass('d-none');
                $form.find('#withdrawal-type-complete').prop('checked', false);
                $form.find('#withdrawal-type-partial').prop('checked', true);
                setWithdrawalTypeMode('partial', $productsContainer);
                updateSubmitState();
            }).fail(function (xhr) {
                const response = xhr.responseJSON || {};

                resetValidationState();
                showError($form, response.message || 'Validation failed.');
            }).always(function () {
                updateValidateButtonState();
            });
        }

        $validateButton.on('click', runOrderValidation);

        if ($form.attr('data-auto-validate-order') === 'true') {
            runOrderValidation();
        }

        $form.on('change', '.js-withdrawal-type', function () {
            const selectedType = $form.find('.js-withdrawal-type:checked').val();
            setWithdrawalTypeMode(selectedType, $productsContainer);
            updateSubmitState();
        });

        $form.on('input', '.js-withdrawal-quantity', function () {
            let $input = $(this);
            let value = Number($input.val());

            const min = Number($input.attr('min')) || 0;
            const max = Number($input.attr('max')) || 0;

            if (Number.isNaN(value)) value = min;

            value = Math.min(Math.max(value, min), max);
            $input.val(value);
            updateSubmitState();
        });

        $form.on('change', '.js-withdrawal-policy', function () {
            updateSubmitState();
        });

        $form.on('submit', function (event) {
            event.preventDefault();

            updateSubmitState();

            if ($submitButton.prop('disabled')) return;

            const products = extractProducts($productsContainer);
            const hasProductRows = $productsContainer.find('.js-withdrawal-product').length > 0;

            if (hasProductRows && !products.length) {
                showError($form, $form.attr('data-error-partial-no-qty') || 'Select at least one product quantity.');

                return;
            }

            $productsJson.val(JSON.stringify(products));

            $.ajax({
                url: submitUrl,
                method: 'POST',
                dataType: 'json',
                data: $form.serialize()
            }).done(function (response) {
                showSuccess($form, response.message || '');

                $form[0].reset();
                $form.find('[name="orderNo"]').val('');

                if (window.history && window.history.replaceState) {
                    const url = new URL(window.location.href);

                    if (url.searchParams.has('orderNo')) {
                        url.searchParams.delete('orderNo');
                        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
                    }
                }

                resetValidationState();
            }).fail(function (xhr) {
                const response = xhr.responseJSON || {};

                showError($form, response.message || 'Submission failed.');
            });
        });
    }
};
